/** @jsxImportSource ./simboluri-jsx */
// «PLAN DE COFRAJ» — vederea Studioului cu colile de cofraj ale soluției: o
// coală pe nivel (planul plăcii de peste nivelul acela, cu stâlpii, pereții și
// grinzile de sub ea, terasa, golurile, elementele care pornesc de pe ea),
// desenată de server din modelul Pasului 7 (`structure/planse/cofraj.py`),
// vizorul colii alese și descărcarea ei — sau a setului întreg — ca DXF / DWG.
//
// Coala aleasă stă în URL (`?coala=`), ca un link s-o deschidă direct.
// Desenele vin pe versiunea setului (`/coli` → `versiune`), deci o coală
// deschisă a doua oară nu se mai cere. Clic pe un element din desen → pagina
// lui, ca în planul de armare.

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useT } from "../i18n";
import {
  getPlanCofrajColi, planCofrajDesenUrl, type ColaPlanArmare, type PlanArmareColi,
} from "./apiStructura";
import { DrawingView } from "./DwgViewer";
import { hashElement } from "./legaturi";
import { Descarcari } from "./PlanArmare";

/** Id-urile de pe coala de cofraj care duc la o pagină de element: grinzile,
 *  stâlpii, lamelele, pereții, ochiurile — nu placa întreagă și nu golurile. */
export function esteElement(id: string): boolean {
  return !id.startsWith("placa:") && !id.startsWith("gol:");
}

export default function PlanCofraj({ projectId, cloneId, solutie }: {
  projectId: string;
  /** spațiul din care se citesc planșele (`useStructure().cloneId`) */
  cloneId: string | null;
  /** referința rândului privit: din ea se fac linkurile spre paginile elementelor */
  solutie?: string | null;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // `undefined` = cererea e pe drum; `null` = spațiul n-are planșe
  const [set, setSet] = useState<PlanArmareColi | null | undefined>(undefined);

  useEffect(() => {
    if (cloneId == null) {
      setSet(null);
      return;
    }
    let viu = true;
    setSet(undefined);
    getPlanCofrajColi(projectId, cloneId)
      .then((c) => { if (viu) setSet(c); })
      .catch(() => { if (viu) setSet(null); });
    return () => { viu = false; };
  }, [projectId, cloneId]);

  const coli: ColaPlanArmare[] = set?.coli ?? [];
  const ceruta = params.get("coala");
  const coala = coli.find((c) => c.id === ceruta) ?? coli[0] ?? null;
  const alege = (id: string) => {
    const q = new URLSearchParams(params);
    q.set("coala", id);
    setParams(q, { replace: true });
  };
  const dwgOk = set?.dwg_disponibil !== false;

  return (
    <div className="pj-step-page" data-testid="plan-cofraj">
      <h2>{t("struct.pc.titlu")}</h2>
      {set === undefined ? (
        <div className="sg-muted">{t("comun.se_incarca")}</div>
      ) : !coli.length ? (
        <div className="sg-muted" style={{ marginTop: 10 }}>{t("struct.pc.gol")}</div>
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <aside className="ob-card" style={{ width: 260, flex: "none", padding: "10px 12px" }}
                 data-testid="lista-coli-cofraj">
            <div style={{ borderBottom: "1px solid var(--line)", paddingBottom: 10,
                          marginBottom: 8 }}>
              <Descarcari projectId={projectId} cloneId={cloneId} coala={coala?.id ?? null}
                          dwgOk={dwgOk} set="plan_cofraj" />
            </div>
            <div className="ob-lbl" style={{ marginBottom: 4 }}>{t("struct.pc.coli_niveluri")}</div>
            {coli.map((c) => {
              const on = coala?.id === c.id;
              return (
                <button key={c.id} type="button"
                        className={`ob-coala${on ? " on" : ""}`}
                        aria-pressed={on}
                        title={c.titlu}
                        onClick={() => alege(c.id)}>
                  {c.titlu}
                </button>
              );
            })}
          </aside>

          <div style={{ flex: 1, minWidth: 0 }}>
            {coala && (
              <div className="ob-card" style={{ overflow: "hidden" }}>
                <div style={{ height: "calc(100vh - 210px)", minHeight: 460 }}>
                  <DrawingView
                    key={coala.id}
                    src={planCofrajDesenUrl(projectId, cloneId, coala.id)}
                    versiune={set?.versiune}
                    title={coala.titlu}
                    embed
                    faraAntet
                    faraBusola
                    onJump={(id) => { if (id && esteElement(id)) navigate(hashElement(projectId, id, solutie)); }}
                    linkEticheta={t("struct.pa.link_element")}
                  />
                </div>
                {coala.elemente.length > 0 && (
                  <div style={{ padding: "10px 14px", borderTop: "1px solid var(--line)",
                                display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {coala.elemente.map((ref) => (
                      <a key={ref} className="ob-chip ob-c-mut" style={{ textDecoration: "none" }}
                         href={hashElement(projectId, ref, solutie)}>
                        {ref}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
